import React, { useState, useMemo, useEffect } from "react";
import {
  FiX,
  FiBookOpen,
  FiCheck,
  FiSearch,
  FiPlus,
  FiChevronDown,
  FiChevronUp,
  FiGitMerge,
} from "react-icons/fi";
import { toast } from "react-toastify";
import {
  createSharedSubject,
  linkSubjectToSharedSubject,
  extractApiErrorMessage,
  getPrimarySubjectTeacher,
} from "../../utils/classGroupApi";
import {
  DEFAULT_ARM_LABEL,
  areSimilarSubjectNames,
  getTeacherName,
  pluralize,
} from "../../utils/classGroupHelpers";

const normalize = (name) => (name || "").trim().toLowerCase();

const ManageSharedSubjectsModal = ({
  isOpen,
  onClose,
  schoolId,
  classGroupId,
  classGroupName = "Class Group",
  memberClasses = [],
  sharedSubjects = [],
  teachers = [],
  subclassLabel = DEFAULT_ARM_LABEL,
  onSuccess,
  onOpenAssignTeacher,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [newSubjectInput, setNewSubjectInput] = useState("");
  const [newSubjectsToAdd, setNewSubjectsToAdd] = useState([]);
  const [expandedSubjectName, setExpandedSubjectName] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState(() => new Set());
  // Similar names the admin chose to treat as one subject: { fromNormName: toNormName }.
  const [mergedInto, setMergedInto] = useState({});
  const [dismissedMerges, setDismissedMerges] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const armLower = subclassLabel.toLowerCase();
  const armsPlural = pluralize(subclassLabel, 2).toLowerCase();

  // Map of existing shared subjects by normalized name
  const existingSharedMap = useMemo(() => {
    const map = new Map();
    (sharedSubjects || []).forEach((s) => {
      const norm = normalize(s.subject_name);
      if (norm) map.set(norm, s);
    });
    return map;
  }, [sharedSubjects]);

  // Every subject found across the arms, grouped by name, before any merges. A copy that is
  // already linked to a shared subject is grouped under that shared subject even if it has a
  // different name (e.g. "Maths" linked to "Mathematics" after an earlier merge), so it isn't
  // offered again as a separate, unshared subject.
  const rawSubjectGroups = useMemo(() => {
    const map = new Map();
    const ensure = (name, extra = {}) => {
      const normName = normalize(name);
      if (!normName) return null;
      if (!map.has(normName)) map.set(normName, { name: name.trim(), normName, entries: [], ...extra });
      return map.get(normName);
    };
    const sharedNameById = new Map(
      (sharedSubjects || []).map((s) => [String(s._id), s.subject_name || ""]),
    );

    memberClasses.forEach((cls) => {
      (cls.subjects || []).forEach((subj) => {
        const linkedSharedName = subj.subject_ref_id
          ? sharedNameById.get(String(subj.subject_ref_id))
          : null;
        const group = ensure(linkedSharedName || subj.subject_name || "");
        if (!group) return;
        group.entries.push({
          classId: cls._id,
          className: cls.class_name,
          groupLabel: cls.group_label,
          subjectId: subj._id,
          subjectName: subj.subject_name,
          subjectRefId: subj.subject_ref_id,
          teacher: getPrimarySubjectTeacher(subj),
        });
      });
    });
    (sharedSubjects || []).forEach((ss) => ensure(ss.subject_name || ""));
    newSubjectsToAdd.forEach((name) => ensure(name, { isBrandNew: true }));
    return map;
  }, [memberClasses, sharedSubjects, newSubjectsToAdd]);

  // Apply the admin's merges ("Maths" folded into "Mathematics", etc.).
  const discoveredSubjectGroups = useMemo(() => {
    const groups = new Map();
    rawSubjectGroups.forEach((g, norm) => {
      if (!mergedInto[norm]) groups.set(norm, { ...g, entries: [...g.entries], mergedNames: [] });
    });
    Object.entries(mergedInto).forEach(([from, to]) => {
      const source = rawSubjectGroups.get(from);
      const target = groups.get(to);
      if (!source || !target) return;
      target.entries.push(...source.entries);
      target.mergedNames.push(source.name);
    });
    return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [rawSubjectGroups, mergedInto]);

  // Pairs of different names that probably mean the same subject, where no arm has both.
  // A merge is only offered when exactly one side is already shared: its copies are then
  // linked to that shared subject. If neither is shared yet, creating the shared subject
  // would also add a second copy to the arms using the other name, so we only advise the
  // admin to rename one of them first.
  const similarNamePairs = useMemo(() => {
    const groups = discoveredSubjectGroups;
    const pairs = [];
    for (let i = 0; i < groups.length; i++) {
      for (let j = i + 1; j < groups.length; j++) {
        const a = groups[i];
        const b = groups[j];
        const key = [a.normName, b.normName].sort().join("|");
        if (dismissedMerges.has(key)) continue;
        if (!areSimilarSubjectNames(a.name, b.name)) continue;
        const aShared = existingSharedMap.has(a.normName);
        const bShared = existingSharedMap.has(b.normName);
        if (aShared && bShared) continue;
        const aClasses = new Set(a.entries.map((e) => e.classId));
        if (b.entries.some((e) => aClasses.has(e.classId))) continue;
        if (aShared || bShared) {
          pairs.push({ key, canMerge: true, keep: aShared ? a : b, fold: aShared ? b : a });
        } else {
          pairs.push({ key, canMerge: false, keep: a, fold: b });
        }
      }
    }
    return pairs;
  }, [discoveredSubjectGroups, existingSharedMap, dismissedMerges]);

  // Initialize only when opening. Parent refetches can replace these arrays while a user is
  // selecting rows; syncing to those new references would erase the in-progress checklist.
  useEffect(() => {
    if (!isOpen) return;
    const initial = new Set();
    (sharedSubjects || []).forEach((s) => {
      const norm = normalize(s.subject_name);
      if (norm) initial.add(norm);
    });
    setSelectedSubjects(initial);
    setMergedInto({});
    setDismissedMerges(new Set());
    setNewSubjectsToAdd([]);
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // A selected subject needs saving if it isn't shared yet, or some arm's copy isn't linked.
  const pendingGroups = useMemo(
    () =>
      discoveredSubjectGroups.filter(
        (g) =>
          selectedSubjects.has(g.normName) &&
          (!existingSharedMap.has(g.normName) || g.entries.some((e) => !e.subjectRefId)),
      ),
    [discoveredSubjectGroups, selectedSubjects, existingSharedMap],
  );
  const pendingNewCount = pendingGroups.filter((g) => !existingSharedMap.has(g.normName)).length;

  const filteredSubjectGroups = useMemo(() => {
    if (!searchTerm.trim()) return discoveredSubjectGroups;
    const term = searchTerm.trim().toLowerCase();
    return discoveredSubjectGroups.filter((g) => g.name.toLowerCase().includes(term));
  }, [discoveredSubjectGroups, searchTerm]);

  if (!isOpen) return null;

  const toggleSubjectShare = (normName) => {
    // Already-shared subjects are locked here — stopping sharing is a separate, confirmed
    // action on the Shared Subjects tab, so unticking can't silently do nothing.
    if (existingSharedMap.has(normName)) return;
    setSelectedSubjects((prev) => {
      const next = new Set(prev);
      if (next.has(normName)) next.delete(normName);
      else next.add(normName);
      return next;
    });
  };

  const acceptMerge = (suggestion) => {
    setMergedInto((prev) => ({ ...prev, [suggestion.fold.normName]: suggestion.keep.normName }));
    setSelectedSubjects((prev) => {
      const next = new Set(prev);
      next.delete(suggestion.fold.normName);
      next.add(suggestion.keep.normName);
      return next;
    });
  };

  const dismissMerge = (suggestion) => {
    setDismissedMerges((prev) => new Set(prev).add(suggestion.key));
  };

  const handleAddNewSubject = (e) => {
    e.preventDefault();
    const trimmed = newSubjectInput.trim();
    if (!trimmed) return;

    const norm = normalize(trimmed);
    if (discoveredSubjectGroups.some((g) => g.normName === norm)) {
      setSelectedSubjects((prev) => new Set(prev).add(norm));
      setNewSubjectInput("");
      return;
    }

    setNewSubjectsToAdd((prev) => [...prev, trimmed]);
    setSelectedSubjects((prev) => new Set(prev).add(norm));
    setNewSubjectInput("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pendingGroups.length === 0) {
      setError("Tick at least one subject that isn't shared yet");
      return;
    }

    try {
      setLoading(true);
      setError("");

      let teacherConflictFound = false;
      let lastSubjectWithConflict = null;
      const linkingFailures = [];

      for (const group of pendingGroups) {
        let sharedDoc = existingSharedMap.get(group.normName);

        if (!sharedDoc) {
          const createRes = await createSharedSubject(schoolId, classGroupId, group.name);
          sharedDoc = createRes?.subject || createRes;
        }

        const sharedId = sharedDoc?._id || sharedDoc?.id;
        const allEntryIds = group.entries.map((entry) => entry.subjectId);

        if (allEntryIds.length > 0 && sharedId) {
          try {
            const linkRes = await linkSubjectToSharedSubject(
              schoolId,
              classGroupId,
              sharedId,
              allEntryIds,
            );
            if (linkRes.teacher_conflict) {
              teacherConflictFound = true;
              lastSubjectWithConflict = sharedDoc;
            }
          } catch (linkErr) {
            console.warn(`Note linking arm subjects for ${group.name}:`, linkErr);
            linkingFailures.push(group.name);
          }
        }
      }

      if (linkingFailures.length > 0) {
        toast.warning(
          `Saved, but ${linkingFailures.join(", ")} couldn't be shared in every ${armLower}. Open this again to finish.`,
        );
      } else {
        toast.success(
          `${pendingGroups.length} subject${pendingGroups.length !== 1 ? "s" : ""} now shared across ${classGroupName}`,
        );
      }

      if (onSuccess) onSuccess();
      onClose();

      // Arms that already had different teachers for a subject: go straight to choosing
      // the subject teacher, where the per-arm breakdown explains what will change.
      if (teacherConflictFound && onOpenAssignTeacher && lastSubjectWithConflict) {
        onOpenAssignTeacher(lastSubjectWithConflict);
      }
    } catch (err) {
      console.error("Error managing shared subjects:", err);
      setError(extractApiErrorMessage(err, "Failed to update shared subjects"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0 bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
              <FiBookOpen className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">Choose shared subjects</h3>
              <p className="text-xs text-slate-500">
                A shared subject has one teacher and one set of tests for every {armLower} in{" "}
                <strong>{classGroupName}</strong>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          {error && (
            <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-700">
              {error}
            </div>
          )}

          {/* Possible duplicates under different names */}
          {similarNamePairs.length > 0 && (
            <div className="space-y-2">
              {similarNamePairs.map((s) => {
                const where = (g) =>
                  g.entries.map((en) => en.groupLabel || en.className).join(", ") || "new";
                return (
                  <div
                    key={s.key}
                    className="p-3 rounded-xl border border-amber-200 bg-amber-50 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
                  >
                    <p className="text-xs text-amber-900">
                      <FiGitMerge className="inline w-3.5 h-3.5 mr-1" />
                      <strong>{s.fold.name}</strong> ({where(s.fold)}) and{" "}
                      <strong>{s.keep.name}</strong> ({where(s.keep)}) look like the same subject.
                      {!s.canMerge &&
                        ` Rename one of them on that ${armLower}'s Subjects page so the names match before sharing — otherwise both will be added to every ${armLower}.`}
                    </p>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => dismissMerge(s)}
                        className="px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 rounded-lg"
                      >
                        {s.canMerge ? "They're different" : "Got it"}
                      </button>
                      {s.canMerge && (
                        <button
                          type="button"
                          onClick={() => acceptMerge(s)}
                          className="px-2.5 py-1 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg"
                        >
                          Share as "{s.keep.name}"
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Search Bar & Quick Controls */}
          {discoveredSubjectGroups.length > 0 && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="relative flex-1">
                <FiSearch className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search subjects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
                />
              </div>

              <button
                type="button"
                onClick={() =>
                  setSelectedSubjects(new Set(discoveredSubjectGroups.map((g) => g.normName)))
                }
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
              >
                Tick all
              </button>
            </div>
          )}

          {/* Discovered Subjects List */}
          {discoveredSubjectGroups.length > 0 && (
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Subjects in your {armsPlural} ({filteredSubjectGroups.length})
              </label>

              {filteredSubjectGroups.length === 0 ? (
                <div className="p-6 text-center border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
                  <p className="text-xs text-slate-500">No matching subjects found</p>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100 max-h-72 overflow-y-auto">
                  {filteredSubjectGroups.map((group) => {
                    const isAlreadyShared = existingSharedMap.has(group.normName);
                    const isSelected = selectedSubjects.has(group.normName);
                    const isExpanded = expandedSubjectName === group.normName;
                    const armCount = new Set(group.entries.map((en) => en.classId)).size;
                    const missingArms = memberClasses.length - armCount;
                    const unlinkedCount = group.entries.filter((en) => !en.subjectRefId).length;

                    let statusText;
                    if (isAlreadyShared && unlinkedCount === 0) {
                      statusText = (
                        <span className="text-slate-500">
                          Shared in {armCount} {pluralize(subclassLabel, armCount).toLowerCase()}.
                          To stop sharing, use the bin icon on the Shared Subjects tab.
                        </span>
                      );
                    } else if (armCount === 0) {
                      statusText = (
                        <span className="text-indigo-600">
                          Will be added to all {memberClasses.length} {armsPlural}
                        </span>
                      );
                    } else {
                      statusText = (
                        <span>
                          In {armCount} {pluralize(subclassLabel, armCount).toLowerCase()}
                          {missingArms > 0 && !isAlreadyShared && (
                            <span className="text-indigo-600">
                              {" "}
                              · will be added to the other {missingArms}
                            </span>
                          )}
                          {isAlreadyShared && unlinkedCount > 0 && (
                            <span className="text-indigo-600">
                              {" "}
                              · {unlinkedCount} {pluralize(subclassLabel, unlinkedCount).toLowerCase()} not
                              connected yet
                            </span>
                          )}
                        </span>
                      );
                    }

                    return (
                      <div
                        key={group.normName}
                        className={`transition-colors ${
                          isSelected ? "bg-indigo-50/30" : "bg-white hover:bg-slate-50/60"
                        }`}
                      >
                        <div className="p-3.5 flex items-center justify-between gap-3">
                          <label
                            className={`flex items-center gap-3 flex-1 min-w-0 ${isAlreadyShared ? "cursor-default" : "cursor-pointer"}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isAlreadyShared}
                              onChange={() => toggleSubjectShare(group.normName)}
                              className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:cursor-default disabled:opacity-60"
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-slate-900 text-sm truncate">
                                  {group.name}
                                </span>
                                {group.mergedNames.length > 0 && (
                                  <span className="text-[11px] text-slate-500">
                                    (includes {group.mergedNames.join(", ")})
                                  </span>
                                )}
                                {isAlreadyShared && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                                    <FiCheck className="w-3 h-3" /> Shared
                                  </span>
                                )}
                                {group.isBrandNew && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                                    New
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5">{statusText}</p>
                            </div>
                          </label>

                          {group.entries.length > 0 && (
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedSubjectName(isExpanded ? null : group.normName)
                              }
                              className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                              title={`See each ${armLower}`}
                            >
                              {isExpanded ? (
                                <FiChevronUp className="w-4 h-4" />
                              ) : (
                                <FiChevronDown className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>

                        {isExpanded && group.entries.length > 0 && (
                          <div className="px-4 pb-3 pt-1 border-t border-slate-100 bg-slate-50/60 text-xs animate-fadeIn">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                              {group.entries.map((entry) => (
                                <div
                                  key={entry.subjectId}
                                  className="p-2 rounded-lg bg-white border border-slate-200 flex items-center justify-between"
                                >
                                  <div>
                                    <span className="font-medium text-slate-800">
                                      {entry.className}
                                    </span>
                                    {normalize(entry.subjectName) !== group.normName && (
                                      <p className="text-[10px] text-slate-500">
                                        Called "{entry.subjectName}" here
                                      </p>
                                    )}
                                    <p className="text-[10px] text-slate-500">
                                      Teacher:{" "}
                                      {entry.teacher
                                        ? getTeacherName(teachers, entry.teacher)
                                        : "none yet"}
                                    </p>
                                  </div>
                                  <span
                                    className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                      entry.subjectRefId
                                        ? "bg-emerald-50 text-emerald-700"
                                        : "bg-slate-100 text-slate-600"
                                    }`}
                                  >
                                    {entry.subjectRefId ? "Shared" : "Not shared yet"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Add Brand New Subject (For subjects not existing in any arm yet) */}
          <div
            className={`p-4 rounded-2xl border space-y-2 ${discoveredSubjectGroups.length === 0 ? "border-indigo-100 bg-indigo-50/60" : "border-slate-200 bg-slate-50/70"}`}
          >
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
              {discoveredSubjectGroups.length === 0
                ? "Add your first shared subject"
                : "Add a subject none of them have yet"}
            </label>
            {discoveredSubjectGroups.length === 0 && (
              <p className="text-xs text-slate-600">
                Your {armsPlural} don't have subjects yet. Add one here and it will be created in
                every {armLower}.
              </p>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={newSubjectInput}
                onChange={(e) => setNewSubjectInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddNewSubject(e);
                  }
                }}
                placeholder="Subject name (e.g. French, Civic Education)"
                className="flex-1 px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                type="button"
                onClick={handleAddNewSubject}
                disabled={!newSubjectInput.trim()}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-xl text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-xs"
              >
                <FiPlus className="w-3.5 h-3.5" />
                Add to list
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50/50 flex-shrink-0">
          <div className="text-xs text-slate-600">
            {pendingGroups.length === 0 ? (
              "Nothing new to save yet"
            ) : (
              <>
                Saving will share <strong>{pendingGroups.length}</strong> subject
                {pendingGroups.length !== 1 ? "s" : ""}
                {pendingNewCount > 0 && ` (${pendingNewCount} new)`} across all {armsPlural}.
              </>
            )}
          </div>

          <div className="flex items-center gap-2.5 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || pendingGroups.length === 0}
              className="px-5 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
              )}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageSharedSubjectsModal;
